import { useMemo, useRef, useState } from 'react';
import type { HistoryResponse, LeagueBundle } from '../../shared/types';
import {
  Avatar,
  Empty,
  ErrorState,
  Legend,
  Loading,
  PageHead,
  PositionTag,
  Section,
  TeamCell,
} from '../components/common';
import { useAllTransactions, useDrafts } from '../lib/api';
import { fmtDate, ordinal, pickLabel } from '../lib/format';
import { buildTeamViews, pickSeasons, sortStandings, summarizePicks, teamIndex } from '../lib/league';
import { managerDirectory, type ManagerRef } from '../lib/rivalries';
import {
  buildLineages,
  buildRosterMaps,
  lineageIndex,
  lineageKey,
  type PickLineage,
} from '../lib/tradetree';
import { Link } from '../lib/router';

/**
 * Keeps grid cells narrow enough that all 9 pick columns fit without the table
 * scrolling on a laptop. The full name stays available as a tooltip, and the
 * row-header column is left untruncated since it has room.
 */
function shortName(name: string): string {
  return name.length <= 13 ? name : `${name.slice(0, 12)}…`;
}

function ManagerChip({ m, hint }: { m: ManagerRef | undefined; hint?: string }) {
  return (
    <span className="tree-node" title={hint}>
      <Avatar id={m?.avatar ?? null} alt={m?.name ?? '?'} />
      <span className="tree-name">{m?.name ?? 'Unknown'}</span>
    </span>
  );
}

function LineageCard({
  lineage,
  dir,
}: {
  lineage: PickLineage;
  dir: Map<string, ManagerRef>;
}) {
  const origin = dir.get(lineage.originalUserId);
  const { outcome } = lineage;

  return (
    <article className="card card-pad tree">
      <div className="tree-head">
        <span className="asset-pick tree-pick">{pickLabel(lineage.season, lineage.round)}</span>
        <span className="faint" style={{ fontSize: 12 }}>
          originally {origin?.name ?? 'unknown'}'s
        </span>
        <span className="faint tree-count">
          {lineage.hops.length} trade{lineage.hops.length === 1 ? '' : 's'}
        </span>
      </div>

      <div className="tree-chain">
        <ManagerChip m={origin} hint="Original owner" />
        {lineage.hops.map((h) => (
          <span key={h.transactionId} className="tree-step">
            <span
              className="tree-arrow"
              title={`Traded in the ${h.tradeSeason} league year, week ${h.week} — ${fmtDate(
                h.created,
              )}`}
            >
              →
            </span>
            <ManagerChip m={h.toUserId ? dir.get(h.toUserId) : undefined} />
          </span>
        ))}
      </div>

      <div className={`tree-outcome ${outcome.kind}`}>
        {outcome.kind === 'drafted' && (
          <>
            <span className="tree-slot">
              {outcome.round}.{String(outcome.draftSlot).padStart(2, '0')}
            </span>
            <PositionTag position={outcome.position} />
            <span className="tree-player">{outcome.playerName}</span>
            <span className="faint" style={{ fontSize: 11.5 }}>
              {outcome.nflTeam ?? 'FA'} · pick {outcome.pickNo} overall
            </span>
          </>
        )}
        {outcome.kind === 'pending' && (
          <span className="faint">The {lineage.season} draft has not happened yet.</span>
        )}
        {outcome.kind === 'unresolved' && (
          <span className="faint">
            Never used. The {lineage.season} draft ran fewer than {lineage.round} round
            {lineage.round === 1 ? '' : 's'}.
          </span>
        )}
      </div>
    </article>
  );
}

function TradeTrees({
  lineages,
  index,
  dir,
  loading,
  error,
  onRetry,
  focus,
  onClearFocus,
}: {
  lineages: PickLineage[];
  index: Map<string, PickLineage>;
  dir: Map<string, ManagerRef>;
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  focus: string | null;
  onClearFocus: () => void;
}) {
  if (loading) return <Loading label="Following every traded pick" />;
  if (error) return <ErrorState message={error} onRetry={onRetry} />;

  const focused = focus ? index.get(focus) : undefined;
  const shown = focused ? [focused] : lineages;
  const resolved = lineages.filter((l) => l.outcome.kind === 'drafted').length;

  return (
    <>
      <div className="toolbar">
        <button className={`chip ${focus ? '' : 'on'}`} onClick={onClearFocus}>
          All picks
          <span className="faint"> {lineages.length}</span>
        </button>
        {focused && (
          <span className="chip on">
            {pickLabel(focused.season, focused.round)} ·{' '}
            {dir.get(focused.originalUserId)?.name ?? 'unknown'}
          </span>
        )}
        <span className="chip" style={{ cursor: 'default' }}>
          Resolved to a player <span className="faint"> {resolved}</span>
        </span>
      </div>

      {shown.length === 0 ? (
        <div className="card">
          <Empty>No draft picks have changed hands yet.</Empty>
        </div>
      ) : (
        <div className="grid grid-2 grid-start">
          {shown.map((l) => (
            <LineageCard key={l.key} lineage={l} dir={dir} />
          ))}
        </div>
      )}
    </>
  );
}

export default function Assets({
  bundle,
  history,
}: {
  bundle: LeagueBundle;
  history: HistoryResponse | null;
}) {
  const views = useMemo(() => sortStandings(buildTeamViews(bundle)), [bundle]);
  const index = useMemo(() => teamIndex(views), [views]);
  const seasons = useMemo(() => pickSeasons(bundle.picks), [bundle.picks]);
  const [season, setSeason] = useState<string | 'all'>('all');
  const [focus, setFocus] = useState<string | null>(null);
  const treeRef = useRef<HTMLDivElement>(null);

  // Trade trees span every season the league has played, so they need one
  // transactions request per league year. Fetched here rather than inside the
  // section below so the ownership grid can mark which cells have a chain.
  const leagueIds = useMemo(() => {
    const ids = [bundle.league.league_id];
    for (const s of history?.seasons ?? []) ids.push(s.league_id);
    return ids;
  }, [bundle.league.league_id, history]);

  const txns = useAllTransactions(leagueIds);
  const drafts = useDrafts();
  const dir = useMemo(() => managerDirectory(bundle, history), [bundle, history]);

  const trees = useMemo(() => {
    if (!txns.data) return [];
    return buildLineages(txns.data, drafts.data, buildRosterMaps(bundle, history, txns.data));
  }, [bundle, history, txns.data, drafts.data]);

  const lineages = useMemo(() => lineageIndex(trees), [trees]);

  const rounds = useMemo(
    () => [...new Set(bundle.picks.map((p) => p.round))].sort((a, b) => a - b),
    [bundle.picks],
  );

  const shownSeasons = season === 'all' ? seasons : [season];

  const cell = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of bundle.picks) {
      m.set(`${p.season}|${p.round}|${p.originalRosterId}`, p.ownerRosterId);
    }
    return m;
  }, [bundle.picks]);

  // Grid rows are keyed by this season's roster ids; lineages are keyed by
  // user_id so they survive Sleeper renumbering. Translate on the way in.
  const currentOwners = useMemo(
    () => new Map(bundle.teams.map((t) => [t.roster_id, t.owner_id])),
    [bundle.teams],
  );

  const keyForCell = (s: string, round: number, rosterId: number): string | null => {
    const userId = currentOwners.get(rosterId);
    return userId ? lineageKey(s, round, userId) : null;
  };

  const openLineage = (key: string) => {
    setFocus(key);
    treeRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  if (bundle.picks.length === 0) {
    return (
      <>
        <PageHead title="Draft Capital" />
        <div className="card">
          <Empty>No draft picks found for this league.</Empty>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHead
        title="Draft Capital"
        meta={[
          `${seasons.length} drafts`,
          `${bundle.league.draft_rounds} rounds`,
          `${bundle.picks.length} picks`,
        ]}
      />

      <Section title="Net Capital" hint="Against owning exactly your own">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Team</th>
                <th className="num">Held</th>
                <th className="num">Own kept</th>
                <th className="num">Acquired</th>
                <th className="num">Traded away</th>
                <th className="num">Net</th>
              </tr>
            </thead>
            <tbody>
              {[...views]
                .map((t) => ({ t, s: summarizePicks(bundle.picks, t.roster_id) }))
                .sort((a, b) => b.s.net - a.s.net || b.s.held.length - a.s.held.length)
                .map(({ t, s }) => (
                  <tr key={t.roster_id}>
                    <td>
                      <TeamCell team={t} />
                    </td>
                    <td className="num">{s.held.length}</td>
                    <td className="num faint">{s.held.length - s.gained.length}</td>
                    {/* Colour only when there is something to colour -- a green
                        em-dash reads as a gain when the real value is zero. */}
                    <td className={`num ${s.gained.length ? 'good' : 'faint'}`}>
                      {s.gained.length ? `+${s.gained.length}` : '—'}
                    </td>
                    <td className={`num ${s.lost.length ? 'bad' : 'faint'}`}>
                      {s.lost.length ? `−${s.lost.length}` : '—'}
                    </td>
                    <td className="num">
                      <span
                        className={`pill ${s.net > 0 ? 'pill-good' : s.net < 0 ? 'pill-bad' : ''}`}
                      >
                        {s.net > 0 ? '+' : ''}
                        {s.net}
                      </span>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </Section>

      <Section
        title="Ownership Grid"
        hint="Row is the original owner, cell is the current holder"
      >
        <div className="toolbar">
          <button
            className={`chip ${season === 'all' ? 'on' : ''}`}
            onClick={() => setSeason('all')}
          >
            All years
          </button>
          {seasons.map((s) => (
            <button key={s} className={`chip ${season === s ? 'on' : ''}`} onClick={() => setSeason(s)}>
              {s}
            </button>
          ))}
        </div>

        <div className="table-scroll">
          <table className="picks-table">
            <thead>
              <tr>
                <th rowSpan={2} style={{ textAlign: 'left' }}>
                  Original owner
                </th>
                {shownSeasons.map((s) => (
                  <th key={s} colSpan={rounds.length} className="year-sep" style={{ textAlign: 'center' }}>
                    {s}
                  </th>
                ))}
              </tr>
              <tr>
                {shownSeasons.flatMap((s) =>
                  rounds.map((r, ri) => (
                    <th
                      key={`${s}-${r}`}
                      className={ri === 0 ? 'year-sep' : ''}
                      style={{ textAlign: 'center' }}
                    >
                      {ordinal(r)}
                    </th>
                  )),
                )}
              </tr>
            </thead>
            <tbody>
              {views.map((row) => (
                <tr key={row.roster_id}>
                  <td style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                    <Link to={`/teams/${row.roster_id}`}>{row.name}</Link>
                  </td>
                  {shownSeasons.flatMap((s) =>
                    rounds.map((r, ri) => {
                      const ownerId = cell.get(`${s}|${r}|${row.roster_id}`);
                      const kept = ownerId === row.roster_id;
                      const owner = ownerId != null ? index.get(ownerId) : undefined;
                      const lk = keyForCell(s, r, row.roster_id);
                      const tree = lk ? lineages.get(lk) : undefined;
                      const label = kept ? '•' : shortName(owner?.name ?? '?');
                      const title = kept
                        ? `${row.name} kept their ${s} ${ordinal(r)}`
                        : `${row.name}'s ${s} ${ordinal(r)} → ${owner?.name ?? 'unknown'}`;

                      return (
                        <td
                          key={`${s}-${r}`}
                          className={`${kept ? 'own' : 'lost'} ${ri === 0 ? 'year-sep' : ''} ${
                            tree ? 'linked' : ''
                          }`}
                          title={
                            tree
                              ? `${title} — ${tree.hops.length} trade${
                                  tree.hops.length === 1 ? '' : 's'
                                }, click to follow it`
                              : title
                          }
                        >
                          {tree && lk ? (
                            <button className="cell-btn" onClick={() => openLineage(lk)}>
                              {label}
                            </button>
                          ) : (
                            label
                          )}
                        </td>
                      );
                    }),
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <Legend
          items={[
            { term: '•', def: 'still holds its own pick' },
            { term: 'Struck through', def: 'traded; the name is the current holder' },
            { term: 'Underlined', def: 'trade chain on record below, click to follow' },
          ]}
        />
      </Section>

      <div ref={treeRef}>
        <Section
          title="Trade Trees"
          hint="What each traded pick became"
        >
          <TradeTrees
            lineages={trees}
            index={lineages}
            dir={dir}
            loading={txns.loading || drafts.loading}
            error={txns.error}
            onRetry={txns.reload}
            focus={focus}
            onClearFocus={() => setFocus(null)}
          />
        </Section>
      </div>
    </>
  );
}
